import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Colors,
  Icon,
  Icons,
  Typography,
  classNames,
  createClasses,
} from '@kp/react-ui';

import { Markdown } from './Markdown';
import { ChatMessage as ChatMessageType } from './types';

const classes = createClasses({
  root: {
    display: 'flex',
    width: '100%',
    gap: '16px',
  },
  userRoot: {
    justifyContent: 'flex-end',
  },
  assistantRoot: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  avatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: Colors.Selected.backgroundStrong,
    flexShrink: 0,
  },
  bubble: {
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: '12px',
    backgroundColor: Colors.Neutral.backgroundStrong,
    color: Colors.Neutral.text,
    transition: 'opacity 0.15s ease',
  },
  bubblePending: {
    opacity: 0.6,
  },
  assistantContent: {
    flex: 1,
    color: Colors.Neutral.text,
  },
  retryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
    padding: '5px 11px',
    borderRadius: '6px',
    border: `1px solid ${Colors.Neutral.border}`,
    backgroundColor: Colors.Neutral.background,
    color: Colors.Neutral.text,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: Colors.Neutral.backgroundHover,
    },
  },
});

interface ChatMessageProps {
  message: ChatMessageType;
  onRetry?: (failedId: string) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onRetry,
}) => {
  const { t } = useTranslation('chat');

  if (!message.content.trim()) return null;

  const isUser = message.role === 'user';
  const { retryId } = message;

  if (isUser) {
    return (
      <div className={classNames(classes.root, classes.userRoot)}>
        <div
          className={classNames(
            classes.bubble,
            message.pending && classes.bubblePending,
          )}
          data-testid={`chat-message-${message.id}`}
        >
          <Markdown>{message.content}</Markdown>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(classes.root, classes.assistantRoot)}>
      <img
        src="/assets/images/ai-chat-avatar.svg"
        alt=""
        className={classes.avatar}
      />
      <div
        className={classes.assistantContent}
        data-testid={`chat-message-${message.id}`}
      >
        <Markdown>{message.content}</Markdown>
        {retryId && onRetry && (
          <button
            type="button"
            className={classes.retryButton}
            onClick={() => onRetry(retryId)}
            data-testid={`chat-retry-${retryId}`}
          >
            <Icon icon={Icons.IconRefresh} size={16} />
            <Typography variant="bodySmall">{t('retry')}</Typography>
          </button>
        )}
      </div>
    </div>
  );
};
